import {Injectable} from '@angular/core';
import {SearchState} from '../types/SearchState';
import {SortConditionsFieldsEnum} from '../enums/SearchEnums';
import {DefaultPage} from '../app-common/const';
import {UserLocationState} from '../types/UserLocationState';
import {ToolsService} from './tools.service';
import {SearchResultContent} from '../types/SearchResultContent';
import {SearchResultsDescription} from '../types/SearchResultsDescription';
import {AppConfigService} from './app-config.service';
import {SearchWidgetState} from '../components/search-widget-v2/typings/search-widget-state';
import {RoutesAddonsEnums} from '../enums/RoutesEnums';

@Injectable({
  providedIn: 'root',
})
export class DefaultValuesService {
  private defaultSearchState: SearchState = {
    caregiverUnitTypeUrlPath: '',
    careServiceTypeUrlPath: '',
    symptomUrlPath: '',
    placeUrlPath: '',
    freeSearchTerm: '',
    sort: SortConditionsFieldsEnum.ES_SCORE,
    page: DefaultPage,
    pageSize: 10,
    totalPages: 1,
    locationSearch: false,
    lat: 0,
    lon: 0,
    openNow: false,
    openWeekend: false,
    customerFeatureType: '',
    isValid: true,
    digitalPage: DefaultPage,
    timeslot: null,
    attributes: {},
    forceContentPage: false,
    forceSearchResults: false,
    urlPath: '',
    distance: null,
    tab: null,
  };

  private defaultSearchWidgetState: SearchWidgetState = {
    numberOfSelectedFilters: 0,
    numberOfSelectedAttributeFilters: 0,
    placeSearchValue: '',
    placeSearchValueLabel: '',
    autocompleteCurrentValue: '',
    autocompleteValueChanged: false,
    autocompletePlaceCurrentValue: '',
    autocompletePlaceValueChanged: false,
    isLocationSearchActive: false,
    isLocationSearchVisible: false,
    askedAboutLocation: false,
    userLat: 0,
    userLon: 0,
    freeSearchFieldFocus: false,
    placesFieldFocus: false,
  };

  private defaultUserLocationState: UserLocationState = {
    locationSearch: false,
    lat: 0,
    lon: 0,
  };

  private cpruTypesWithAvailableTimesSorting: string[] = [
    'tandhygienist',
    'hudläkare',
    'gynekolog',
    'tandläkare',
    'naprapat',
    'kiropraktor',
    'provtagningsverksamhet',
    'skönhetsvård',
    'audionom',
  ];

  private serviceTypesWithAvailableTimesSorting: string[] = [
    'tandläkare',
    'tandläkarundersökning',
    'tandhygienist',
    'tandhygienistundersökning',
    'tandblekning',
    'akut-tandvård',
    'audionom',
    'bettskena',
    'estetisk-tandvård',
    'pigmentfläckar',
    'graviditetstest',
    'gynekolog',
    'gynekologisk-specialistläkarmottagning',
    'gynekologisk-undersökning',
    'hudläkare',
    'hudvård',
    'hälsporre',
    'hälsprickor',
    'hörapparater',
    'hörselskydd',
    'hörseltest',
    'kiropraktor',
    'kiropraktorbehandling',
    'kvinnlig-gynekolog',
    'massage',
    'naprapatbehandling',
    'rotfyllning',
    'ryggskott',
    'sista-minuten-tandvård',
    'sjukgymnast',
    'stötvågsbehandling',
    'tandimplantat',
    'tandlagning',
    'tandlossning',
    'tandsten',
    'tandställning',
    'tandutdragning',
    'tandvård-för-dig-med-tandläkarskräck',
    'tandvårdsspecialist',
  ];

  private searchResultContent: SearchResultContent = {
    allSolved: false,
    breadcrumbs: [],
    caregiverUnitType: null,
    careServiceType: null,
    freeText: '',
    parentPlace: '',
    place: null,
    symptom: null,
  };

  private searchResultDescription: SearchResultsDescription = {
    description: '',
    introduction: '',
    title: '',
  };

  constructor(
    private tools: ToolsService,
    private appConfigService: AppConfigService,
  ) {
    this.defaultSearchState.pageSize = appConfigService.appConfig.pageSize;
  }

  public getDefaultSearchWidgetState(): SearchWidgetState {
    return this.tools.clone(this.defaultSearchWidgetState);
  }

  public getDefaultSearchState(
    caregiverUnitTypeUrlPath?: string,
    careServiceTypeUrlPath?: string,
    symptomUrlPath?: string,
    freeSearchTerm?: string,
  ): SearchState {
    const state: SearchState = this.tools.clone(this.defaultSearchState);
    if (
      !this.tools.isNil(careServiceTypeUrlPath) &&
      careServiceTypeUrlPath !== '' &&
      !this.tools.isNil(caregiverUnitTypeUrlPath) &&
      caregiverUnitTypeUrlPath === '' &&
      !this.tools.isNil(symptomUrlPath) &&
      symptomUrlPath === '' &&
      !this.tools.isNil(freeSearchTerm) &&
      freeSearchTerm === ''
    ) {
      state.tab = RoutesAddonsEnums.SERP_SERVICES;
    } else if (
      (!this.tools.isNil(caregiverUnitTypeUrlPath) && caregiverUnitTypeUrlPath !== '') ||
      (!this.tools.isNil(symptomUrlPath) && symptomUrlPath !== '') ||
      (!this.tools.isNil(freeSearchTerm) && freeSearchTerm !== '')
    ) {
      state.tab = RoutesAddonsEnums.SERP_CAREPROVIDERS;
    }
    return state;
  }

  public getDefaultUserLocationState(): UserLocationState {
    return this.defaultUserLocationState;
  }

  public getCpruTypesWithAvailableTimesSorting(): string[] {
    return this.cpruTypesWithAvailableTimesSorting;
  }

  public getCareServiceTypesWithAvailableTimesSorting(): string[] {
    return this.serviceTypesWithAvailableTimesSorting;
  }

  public getSearchResultContent(): SearchResultContent {
    return this.searchResultContent;
  }

  public getSearchResultDescription(): SearchResultsDescription {
    return this.searchResultDescription;
  }
}
